// client/src/components/Support/SupportPage.js
// User-facing support ticket submission and tracking

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Card, CardContent, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, Chip, Stack, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  CircularProgress, Alert, Tabs, Tab, useTheme, useMediaQuery,
  IconButton, Tooltip
} from '@mui/material';
import {
  Plus as Add, RefreshCw as Refresh, Send, ChevronRight, CheckCircle, Clock,
  AlertCircle, MessageSquare, HelpCircle, Bug, CreditCard, Lightbulb
} from 'lucide-react';
import { createTicket, getUserTickets, getTicketById, addReply } from '../../services/supportAPI';

const CATEGORY_CONFIG = {
  bug: { label: '🐛 Bug Report', icon: Bug, color: '#ef4444' },
  billing: { label: '💳 Billing Issue', icon: CreditCard, color: '#f59e0b' },
  account: { label: '👤 Account Problem', icon: AlertCircle, color: '#8b5cf6' },
  feature: { label: '✨ Feature Request', icon: Lightbulb, color: '#10b981' },
  general: { label: '💬 General Question', icon: HelpCircle, color: '#3b82f6' },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: '#6b7280' },
  medium: { label: 'Medium', color: '#3b82f6' },
  high: { label: 'High', color: '#f59e0b' },
  urgent: { label: 'Urgent', color: '#ef4444' },
};

const STATUS_CONFIG = {
  open: { label: 'Open', color: '#ef4444', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: '#f59e0b', icon: Clock },
  waiting_user: { label: 'Waiting for your reply', color: '#8b5cf6', icon: MessageSquare },
  resolved: { label: 'Resolved', color: '#10b981', icon: CheckCircle },
  closed: { label: 'Closed', color: '#6b7280', icon: CheckCircle },
};

function TicketStatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return (
    <Chip label={cfg.label} size="small"
      sx={{ bgcolor: `${cfg.color}20`, color: cfg.color, fontWeight: 700, fontSize: '0.7rem' }} />
  );
}

function NewTicketForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ subject: '', category: 'general', priority: 'medium', message: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      setError('Subject and message are required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await createTicket(form);
      onCreated(res?.data || res);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to submit ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>New Support Ticket</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack spacing={2.5}>
        <TextField label="Subject" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
          placeholder="Brief description of your issue" fullWidth required />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select value={form.category} label="Category" onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {Object.entries(CATEGORY_CONFIG).map(([val, cfg]) => (
                <MenuItem key={val} value={val}>{cfg.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select value={form.priority} label="Priority" onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
              {Object.entries(PRIORITY_CONFIG).map(([val, cfg]) => (
                <MenuItem key={val} value={val}>
                  <Box component="span" sx={{ color: cfg.color, fontWeight: 600 }}>{cfg.label}</Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <TextField label="Describe your issue" value={form.message}
          onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
          multiline rows={6} fullWidth required
          placeholder="Please provide as much detail as possible — steps to reproduce, screenshots description, account details..." />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {onCancel && <Button onClick={onCancel} variant="outlined">Cancel</Button>}
          <Button onClick={handleSubmit} variant="contained" disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
            sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', borderRadius: 2, px: 3 }}>
            {loading ? 'Submitting...' : 'Submit Ticket'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function TicketDetailDialog({ ticketId, open, onClose, onUpdate }) {
  const theme = useTheme();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  useEffect(() => {
    if (!open || !ticketId) return;
    setLoading(true);
    getTicketById(ticketId)
      .then(r => setTicket(r?.data || r))
      .catch(() => setTicket(null))
      .finally(() => setLoading(false));
  }, [open, ticketId]);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      const res = await addReply(ticketId, replyText);
      setTicket(res?.data || res);
      setReplyText('');
      if (onUpdate) onUpdate();
    } catch { } finally { setReplying(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        {ticket ? (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" fontWeight={700} sx={{ color: '#6366f1' }}>{ticket.ticketNumber}</Typography>
              <TicketStatusChip status={ticket.status} />
            </Stack>
            <Typography variant="h6" fontWeight={700}>{ticket.subject}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip label={CATEGORY_CONFIG[ticket.category]?.label || ticket.category} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              <Chip label={`${PRIORITY_CONFIG[ticket.priority]?.label || ticket.priority} Priority`} size="small"
                sx={{ bgcolor: `${PRIORITY_CONFIG[ticket.priority]?.color}20`, color: PRIORITY_CONFIG[ticket.priority]?.color, fontSize: '0.7rem' }} />
            </Stack>
          </Box>
        ) : 'Loading...'}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : !ticket ? (
          <Alert severity="error">Failed to load ticket.</Alert>
        ) : (
          <Box>
            {/* Original message */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: '#6366f120', color: '#6366f1', fontSize: '0.7rem' }}>You</Avatar>
                <Typography variant="caption" fontWeight={700}>You</Typography>
                <Typography variant="caption" color="text.secondary">· {new Date(ticket.createdAt).toLocaleString()}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{ticket.message}</Typography>
            </Box>

            {/* Replies */}
            {(ticket.replies || []).map((reply, i) => (
              <Box key={i} sx={{
                mb: 1.5, p: 2, borderRadius: 2,
                ml: reply.authorRole === 'admin' ? 0 : 4,
                mr: reply.authorRole === 'admin' ? 4 : 0,
                bgcolor: reply.authorRole === 'admin' ? `${'#6366f1'}10` : theme.palette.action.hover,
                border: `1px solid ${reply.authorRole === 'admin' ? '#6366f130' : theme.palette.divider}`
              }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: reply.authorRole === 'admin' ? '#6366f1' : '#10b981', fontSize: '0.65rem' }}>
                    {reply.authorRole === 'admin' ? 'S' : 'Y'}
                  </Avatar>
                  <Typography variant="caption" fontWeight={700}>
                    {reply.authorRole === 'admin' ? 'Support Team' : 'You'}
                  </Typography>
                  {reply.authorRole === 'admin' && (
                    <Chip label="Staff" size="small" sx={{ fontSize: '0.6rem', height: 16, bgcolor: '#6366f120', color: '#6366f1' }} />
                  )}
                  <Typography variant="caption" color="text.secondary">{new Date(reply.createdAt).toLocaleString()}</Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{reply.message}</Typography>
              </Box>
            ))}

            {/* Reply input (only if not resolved/closed) */}
            {!['resolved', 'closed'].includes(ticket.status) && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Add a reply</Typography>
                <TextField multiline rows={3} fullWidth value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Type your message to the support team..." />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {ticket && !['resolved', 'closed'].includes(ticket.status) && (
          <Button variant="contained" disabled={!replyText.trim() || replying} onClick={handleReply}
            startIcon={replying ? <CircularProgress size={14} color="inherit" /> : <Send size={14} />}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
            {replying ? 'Sending...' : 'Send Reply'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function MyTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUserTickets();
      const arr = res?.data || res;
      setTickets(Array.isArray(arr) ? arr : []);
    } catch (e) {
      setError('Failed to load your tickets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert>;

  if (tickets.length === 0) {
    return (
      <Card sx={{ textAlign: 'center', py: 8 }}>
        <MessageSquare size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
        <Typography variant="h6" color="text.secondary">No tickets yet</Typography>
        <Typography variant="body2" color="text.secondary">Submit a ticket to get help from our support team.</Typography>
      </Card>
    );
  }

  return (
    <>
      <Stack spacing={1.5}>
        {tickets.map(t => {
          const catCfg = CATEGORY_CONFIG[t.category] || CATEGORY_CONFIG.general;
          const lastReply = t.replies?.[t.replies.length - 1];
          return (
            <Card key={t._id} onClick={() => { setSelectedId(t._id); setDetailOpen(true); }}
              sx={{ cursor: 'pointer', border: `1px solid`, borderColor: t.status === 'waiting_user' ? '#8b5cf660' : 'divider',
                bgcolor: t.status === 'waiting_user' ? '#8b5cf608' : 'background.paper',
                '&:hover': { borderColor: '#6366f1', boxShadow: '0 2px 12px rgba(99,102,241,0.1)' }, transition: 'all 0.2s' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: `${catCfg.color}15`, color: catCfg.color, flexShrink: 0, mt: 0.25 }}>
                      <catCfg.icon size={16} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                        <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 700 }}>{t.ticketNumber}</Typography>
                        {t.status === 'waiting_user' && (
                          <Chip label="Your reply needed" size="small"
                            sx={{ bgcolor: '#8b5cf620', color: '#8b5cf6', fontSize: '0.6rem', height: 18, fontWeight: 700 }} />
                        )}
                      </Stack>
                      <Typography fontWeight={600} noWrap>{t.subject}</Typography>
                      {lastReply && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          Last reply: {lastReply.authorRole === 'admin' ? 'Support Team' : 'You'} · {new Date(lastReply.createdAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0, ml: 1 }}>
                    <TicketStatusChip status={t.status} />
                    <Typography variant="caption" color="text.secondary">{new Date(t.createdAt).toLocaleDateString()}</Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <TicketDetailDialog
        ticketId={selectedId}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedId(null); }}
        onUpdate={load}
      />
    </>
  );
}

export default function SupportPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState(0);
  const [successMsg, setSuccessMsg] = useState(null);

  const handleCreated = (ticket) => {
    setSuccessMsg(`Ticket ${ticket?.ticketNumber || ''} submitted! Our team will respond within 24 hours.`);
    setTab(0);
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{
        borderRadius: 4, mb: 4, p: { xs: 3, md: 4 },
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)',
        color: '#fff'
      }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <MessageSquare size={32} />
          <Box>
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={900}>Support Center</Typography>
            <Typography sx={{ opacity: 0.85, fontSize: '0.9rem' }}>
              Get help from our team — we typically respond within 24 hours
            </Typography>
          </Box>
        </Stack>
      </Box>

      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 3, borderRadius: 2 }}>
          {successMsg}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ '& .MuiTabs-indicator': { bgcolor: '#6366f1' }, '& .Mui-selected': { color: '#6366f1' } }}>
          <Tab label="My Tickets" />
          <Tab label="New Ticket" />
        </Tabs>
      </Card>

      {tab === 0 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>Your support history</Typography>
            <Button size="small" startIcon={<Add size={14} />} variant="contained"
              onClick={() => setTab(1)}
              sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
              New Ticket
            </Button>
          </Stack>
          <MyTickets />
        </Box>
      )}

      {tab === 1 && (
        <Card sx={{ p: 3 }}>
          <NewTicketForm onCreated={handleCreated} onCancel={() => setTab(0)} />
        </Card>
      )}
    </Container>
  );
}
